// Persistence layer for the data folder. Mirrors the desktop app's layout
// so a folder produced by either product is readable by the other:
//
//   <folder>/
//     categories.json
//     category_cache.json
//     processed/<YYYY-MM>.json
//     dashboard_data.json
//
// The processed/<month>.json files are the source of truth. dashboard_data.json
// is a regenerated bundle the dashboard reads in one shot; it's safe to delete
// (we'll rebuild it from the processed files on next save).

import type { Transaction } from "./common.js";
import { monthKey } from "./common.js";
import {
  readJsonFile,
  writeJsonFile,
} from "./folder.js";
import {
  type Bundle,
  type MonthBundle,
  summarize,
} from "./bundle.js";
import { detectDuplicates, mergeIssues, type ReconIssue } from "./reconcile.js";
import type { CategoryCache } from "./categorize.js";
import { DEFAULT_CATEGORIES, type CategoriesDoc } from "./seeds.js";
import * as log from "./log.js";

// --- shape of one persisted month file. Stable across desktop + web. ---
interface PersistedMonth {
  month: string;
  transactions: Transaction[];
  files_seen?: Array<{ file: string; portal: string }>;
  recon?: { issues: ReconIssue[] };
}

export interface DataFolderState {
  categories: CategoriesDoc;
  cache: CategoryCache;
  bundle: Bundle;
  /** Months that exist on disk. Lets us know what to read/write. */
  knownMonths: string[];
}

// Iterate through processed/ and load every <YYYY-MM>.json. Missing dir or
// missing files → empty state, not an error.
async function loadAllProcessedMonths(
  folder: FileSystemDirectoryHandle,
): Promise<{ months: MonthBundle[]; knownMonths: string[] }> {
  const months: MonthBundle[] = [];
  const knownMonths: string[] = [];
  let processedDir: FileSystemDirectoryHandle;
  try {
    processedDir = await folder.getDirectoryHandle("processed");
  } catch {
    return { months, knownMonths };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for await (const [name, entry] of (processedDir as any).entries()) {
    if (entry.kind !== "file" || !/^\d{4}-\d{2}\.json$/.test(name)) continue;
    try {
      const f = await (entry as FileSystemFileHandle).getFile();
      const doc = JSON.parse(await f.text()) as PersistedMonth;
      const month = doc.month || name.replace(/\.json$/, "");
      const txs = Array.isArray(doc.transactions) ? doc.transactions : [];
      // Re-detect on load — handles both the desktop-written shape
      // (which uses the same detector) and any in-flight schema drift.
      // Status from the persisted issues is carried forward where the
      // tx-id sets still match.
      const fresh = detectDuplicates(txs);
      const merged = mergeIssues(fresh, (doc.recon?.issues as ReconIssue[]) ?? []);
      months.push({
        month,
        transactions: txs,
        files_seen: doc.files_seen ?? [],
        recon: { issues: merged },
        summary: summarize(txs),
      });
      knownMonths.push(month);
    } catch (err) {
      // Per-file corruption tolerance — log + skip, mirror desktop behavior.
      log.warn("state", `Skipping corrupt ${name}`, { error: String(err) });
    }
  }
  months.sort((a, b) => a.month.localeCompare(b.month));
  knownMonths.sort();
  return { months, knownMonths };
}

// Load the full state from a freshly-picked folder. Seeds categories.json
// when missing so editing works from the very first session, even on a
// folder that's never seen the desktop app.
export async function loadDataFolder(
  folder: FileSystemDirectoryHandle,
): Promise<DataFolderState> {
  let categories =
    (await readJsonFile<CategoriesDoc>(folder, "categories.json")) ?? null;
  if (!categories) {
    categories = JSON.parse(JSON.stringify(DEFAULT_CATEGORIES));
    await writeJsonFile(folder, "categories.json", categories);
    log.info("state", "Seeded categories.json (folder had none)");
  }
  const cache =
    (await readJsonFile<CategoryCache>(folder, "category_cache.json")) ?? {};
  const { months, knownMonths } = await loadAllProcessedMonths(folder);
  const txCount = months.reduce((n, m) => n + m.transactions.length, 0);
  log.info("state", `Loaded folder "${folder.name}"`, {
    months_on_disk: knownMonths.length,
    transactions: txCount,
    cached_vendors: Object.keys(cache).length,
  });
  const bundle: Bundle = {
    months,
    categories,
    generated_at: new Date().toISOString(),
  };
  return { categories: categories as CategoriesDoc, cache, bundle, knownMonths };
}

// Merge fresh transactions from a process run into the in-memory bundle.
// Dedup by Transaction.id (matches scripts/process_month.py — first import
// wins, later imports don't overwrite). Returns the set of months whose
// contents changed so the caller only re-writes those.
export function mergeIntoBundle(
  bundle: Bundle,
  fresh: Transaction[],
  newFilesSeen: Map<string, Array<{ file: string; portal: string }>>,
): Set<string> {
  // Index existing transactions by id, and by month for fast lookup.
  const existingIds = new Set<string>();
  const byMonth = new Map<string, MonthBundle>();
  for (const m of bundle.months) {
    byMonth.set(m.month, m);
    for (const t of m.transactions) existingIds.add(t.id);
  }
  const touched = new Set<string>();
  for (const t of fresh) {
    if (existingIds.has(t.id)) continue;
    existingIds.add(t.id);
    const key = monthKey(t.date);
    let m = byMonth.get(key);
    if (!m) {
      m = {
        month: key,
        transactions: [],
        files_seen: [],
        recon: { issues: [] },
        summary: summarize([]),
      };
      byMonth.set(key, m);
      bundle.months.push(m);
    }
    m.transactions.push(t);
    touched.add(key);
  }
  // Merge files_seen for touched months.
  for (const [month, files] of newFilesSeen) {
    const m = byMonth.get(month);
    if (!m) continue;
    const seen = new Set(m.files_seen.map((f) => f.file));
    for (const f of files) {
      if (!seen.has(f.file)) {
        m.files_seen.push(f);
        seen.add(f.file);
      }
    }
  }
  // Re-summarize + re-detect duplicates on touched months. Carry forward
  // any "resolved" status the user set previously on still-matching issue
  // tx-id sets.
  for (const month of touched) {
    const m = byMonth.get(month)!;
    m.summary = summarize(m.transactions);
    const fresh = detectDuplicates(m.transactions);
    m.recon = { issues: mergeIssues(fresh, m.recon?.issues ?? []) };
  }
  bundle.months.sort((a, b) => a.month.localeCompare(b.month));
  bundle.generated_at = new Date().toISOString();
  return touched;
}

/** Apply a set of Plaid `removed` transaction_ids against the bundle.
 *  For each transaction whose `raw.plaid_transaction_id` is in the set,
 *  flip `deleted = true` (idempotent — already-deleted rows stay flipped).
 *  Returns the set of months whose contents changed so persistAfterBatch
 *  can re-write just those.
 *
 *  Plaid's /transactions/sync uses `removed[]` to signal transactions
 *  the bank corrected or reversed (most often: pending charges that
 *  never posted). Without applying this signal, the dashboard
 *  accumulates ghost rows that don't match the user's real bank
 *  statement.
 *
 *  We don't physically remove the row — we mark it `deleted: true` so
 *  it disappears from the default "active" filter but is recoverable
 *  via "all" / "only-deleted". Matches what the manual delete button
 *  does. */
export function applyPlaidRemoved(bundle: Bundle, removedPlaidIds: Iterable<string>): Set<string> {
  const ids = removedPlaidIds instanceof Set ? removedPlaidIds : new Set(removedPlaidIds);
  const touched = new Set<string>();
  if (ids.size === 0) return touched;
  for (const m of bundle.months) {
    let changed = false;
    for (const t of m.transactions) {
      const plaidId = (t.raw?.plaid_transaction_id ?? "") as string;
      if (!plaidId) continue;
      if (!ids.has(plaidId)) continue;
      if (t.deleted) continue;
      t.deleted = true;
      changed = true;
    }
    if (changed) {
      m.summary = summarize(m.transactions);
      touched.add(m.month);
    }
  }
  return touched;
}

// Persist a set of months back to disk as processed/<month>.json. Caller
// is expected to pass the in-memory bundle that already has the updated
// transactions; this just serializes.
export async function saveMonths(
  folder: FileSystemDirectoryHandle,
  bundle: Bundle,
  months: Iterable<string>,
): Promise<void> {
  const monthMap = new Map(bundle.months.map((m) => [m.month, m]));
  for (const month of months) {
    const m = monthMap.get(month);
    if (!m) continue;
    const doc: PersistedMonth = {
      month: m.month,
      transactions: m.transactions,
      files_seen: m.files_seen,
      recon: m.recon,
    };
    await writeJsonFile(folder, `processed/${month}.json`, doc);
  }
}

// Persist the dashboard_data.json bundle in one shot. This is the file the
// desktop dashboard reads via invoke("load_bundle"); the web app produces
// it for compatibility and so a fresh page-load can render fast.
export async function saveDashboardBundle(
  folder: FileSystemDirectoryHandle,
  bundle: Bundle,
): Promise<void> {
  await writeJsonFile(folder, "dashboard_data.json", bundle);
}

// Persist the category cache (vendor_key → assignment). The whole cache
// is rewritten atomically; size is small (typically <50 KB even for years
// of statements) so this is fine.
export async function saveCategoryCache(
  folder: FileSystemDirectoryHandle,
  cache: CategoryCache,
): Promise<void> {
  await writeJsonFile(folder, "category_cache.json", cache);
}

// ─────────────────────────────────────────────────────────────────────
// User-extensible taxonomy: add/remove categories + subcategories at runtime.
// All four helpers mutate `doc` in place AND persist to disk, so the caller's
// in-memory reference (main.ts's `categories`) keeps working without reload.
// ─────────────────────────────────────────────────────────────────────

export class TaxonomyError extends Error {
  // Distinct from generic Error so the UI can surface validation messages
  // separately from "your disk is full" etc.
}

const RESERVED_CATEGORIES = new Set(["Uncategorized"]);

export async function addCategory(
  folder: FileSystemDirectoryHandle,
  doc: CategoriesDoc,
  def: { name: string; kind: "income" | "expense" | "transfer"; subcategories: string[] },
): Promise<CategoriesDoc> {
  const name = def.name.trim();
  if (!name) throw new TaxonomyError("Category name is required");
  if (doc.categories.some((c) => c.name === name)) {
    throw new TaxonomyError(`Category "${name}" already exists`);
  }
  // Dedupe + trim subcategory names. Fall back to "Needs Review" so a
  // category with no subcategories is still selectable in the picker.
  const seen = new Set<string>();
  const subs: string[] = [];
  for (const s of def.subcategories) {
    const t = s.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    subs.push(t);
  }
  doc.categories.push({
    name,
    kind: def.kind,
    subcategories: subs.length > 0 ? subs : ["Needs Review"],
  });
  await writeJsonFile(folder, "categories.json", doc);
  log.info("state", `Added category "${name}"`, {
    kind: def.kind,
    subcategories: subs.length,
  });
  return doc;
}

export async function addSubcategory(
  folder: FileSystemDirectoryHandle,
  doc: CategoriesDoc,
  parentName: string,
  subName: string,
): Promise<CategoriesDoc> {
  const cat = doc.categories.find((c) => c.name === parentName);
  if (!cat) throw new TaxonomyError(`Category "${parentName}" not found`);
  const sub = subName.trim();
  if (!sub) throw new TaxonomyError("Subcategory name is required");
  if (cat.subcategories.includes(sub)) return doc; // idempotent, no error
  cat.subcategories.push(sub);
  await writeJsonFile(folder, "categories.json", doc);
  log.info("state", `Added subcategory`, { parent: parentName, sub });
  return doc;
}

export async function removeSubcategory(
  folder: FileSystemDirectoryHandle,
  doc: CategoriesDoc,
  parentName: string,
  subName: string,
): Promise<CategoriesDoc> {
  const cat = doc.categories.find((c) => c.name === parentName);
  if (!cat) throw new TaxonomyError(`Category "${parentName}" not found`);
  // Reserve "Needs Review" under Uncategorized — it's the loader default.
  if (parentName === "Uncategorized" && subName === "Needs Review") {
    throw new TaxonomyError(
      "Cannot remove 'Uncategorized / Needs Review' — it's the loader default for new transactions.",
    );
  }
  cat.subcategories = cat.subcategories.filter((s) => s !== subName);
  await writeJsonFile(folder, "categories.json", doc);
  log.info("state", `Removed subcategory`, { parent: parentName, sub: subName });
  return doc;
}

export async function removeCategory(
  folder: FileSystemDirectoryHandle,
  doc: CategoriesDoc,
  name: string,
): Promise<CategoriesDoc> {
  if (RESERVED_CATEGORIES.has(name)) {
    throw new TaxonomyError(`Cannot remove reserved category "${name}"`);
  }
  const before = doc.categories.length;
  doc.categories = doc.categories.filter((c) => c.name !== name);
  if (doc.categories.length === before) {
    throw new TaxonomyError(`Category "${name}" not found`);
  }
  await writeJsonFile(folder, "categories.json", doc);
  log.info("state", `Removed category "${name}"`);
  return doc;
}

// Update a single transaction's category. Persists first; mutates the
// in-memory bundle only AFTER all three writes succeed. If any write
// throws, the in-memory state is untouched — caller sees the error and
// the UI reflects what's actually on disk.
//
// Why this ordering matters: previously we mutated in-place THEN wrote,
// so a mid-sequence write failure left memory ahead of disk and the
// next edit operated on stale data. Disk is now the source of truth
// for "did this commit?"
export async function updateTransactionCategory(
  folder: FileSystemDirectoryHandle,
  bundle: Bundle,
  cache: CategoryCache,
  txId: string,
  category: string,
  subcategory: string,
): Promise<{ month: string; updated: Transaction } | null> {
  // Find the tx + its owning month without mutating anything.
  let target: { m: MonthBundle; tx: Transaction } | null = null;
  for (const m of bundle.months) {
    const tx = m.transactions.find((t) => t.id === txId);
    if (tx) { target = { m, tx }; break; }
  }
  if (!target) return null;
  const { m, tx } = target;

  // Build the prospective new state in temporary structures. We persist
  // these THEN apply to the live objects only on success.
  const proposedTransactions = m.transactions.map((t) =>
    t.id === txId ? { ...t, category, subcategory } : t,
  );
  const proposedMonth: MonthBundle = {
    ...m,
    transactions: proposedTransactions,
    summary: summarize(proposedTransactions),
  };
  const proposedBundle: Bundle = {
    ...bundle,
    months: bundle.months.map((mm) => (mm.month === m.month ? proposedMonth : mm)),
    generated_at: new Date().toISOString(),
  };
  const proposedCache: CategoryCache = { ...cache };
  if (tx.vendor_key) proposedCache[tx.vendor_key] = { category, subcategory };

  // Write attempt — any throw aborts before in-memory state is touched.
  try {
    await saveMonths(folder, proposedBundle, [m.month]);
    await saveDashboardBundle(folder, proposedBundle);
    await saveCategoryCache(folder, proposedCache);
  } catch (err) {
    log.error("state", `Recategorize persist failed; in-memory state unchanged`, {
      tx_id: txId, error: String(err),
    });
    throw err;
  }

  // Persistence succeeded — apply to live objects so the UI re-renders
  // the new value.
  tx.category = category;
  tx.subcategory = subcategory;
  m.summary = proposedMonth.summary;
  if (tx.vendor_key) cache[tx.vendor_key] = { category, subcategory };
  bundle.generated_at = proposedBundle.generated_at;

  log.info("state", `Recategorized ${tx.vendor_raw}`, {
    tx_id: txId,
    vendor_key: tx.vendor_key,
    category, subcategory, month: m.month,
  });
  return { month: m.month, updated: tx };
}

// Mark a transaction as deleted. Mirrors edits.py:delete on desktop —
// sets tx.deleted=true, re-summarizes the month, persists. Same write-
// first-then-mutate pattern as updateTransactionCategory: if a write
// throws, in-memory state is untouched.
export async function deleteTransaction(
  folder: FileSystemDirectoryHandle,
  bundle: Bundle,
  cache: CategoryCache,
  txId: string,
  deleted = true,
): Promise<{ month: string; updated: Transaction } | null> {
  let target: { m: MonthBundle; tx: Transaction } | null = null;
  for (const m of bundle.months) {
    const tx = m.transactions.find((t) => t.id === txId);
    if (tx) { target = { m, tx }; break; }
  }
  if (!target) return null;
  const { m, tx } = target;

  const proposedTransactions = m.transactions.map((t) =>
    t.id === txId ? { ...t, deleted } : t,
  );
  const proposedMonth: MonthBundle = {
    ...m,
    transactions: proposedTransactions,
    summary: summarize(proposedTransactions),
  };
  const proposedBundle: Bundle = {
    ...bundle,
    months: bundle.months.map((mm) => (mm.month === m.month ? proposedMonth : mm)),
    generated_at: new Date().toISOString(),
  };

  try {
    await saveMonths(folder, proposedBundle, [m.month]);
    await saveDashboardBundle(folder, proposedBundle);
  } catch (err) {
    log.error("state", `Delete-tx persist failed; in-memory state unchanged`, {
      tx_id: txId, error: String(err),
    });
    throw err;
  }

  tx.deleted = deleted;
  m.summary = proposedMonth.summary;
  bundle.generated_at = proposedBundle.generated_at;
  void cache; // unused — kept in signature for parity with updateTransactionCategory
  log.info("state", deleted ? `Deleted ${tx.vendor_raw}` : `Restored ${tx.vendor_raw}`, {
    tx_id: txId, month: m.month,
  });
  return { month: m.month, updated: tx };
}

// Convenience for the orchestrator: persist the whole post-batch state.
export async function persistAfterBatch(
  folder: FileSystemDirectoryHandle,
  bundle: Bundle,
  cache: CategoryCache,
  touched: Set<string>,
): Promise<void> {
  if (touched.size > 0) await saveMonths(folder, bundle, touched);
  await saveDashboardBundle(folder, bundle);
  await saveCategoryCache(folder, cache);
  log.info("state", "Persisted batch to disk", {
    months_written: [...touched].sort(),
    total_months: bundle.months.length,
  });
}

// Empty bundle factory used before the first folder is picked, OR when a
// folder has no processed/ directory yet. Keeps render code happy without
// having to null-check everywhere.
export function emptyBundle(categories: CategoriesDoc | null = null): Bundle {
  return {
    months: [],
    categories: categories ?? DEFAULT_CATEGORIES,
    generated_at: new Date().toISOString(),
  };
}

// Group freshly-loaded files_seen by month (the orchestrator returns a flat
// per-file list; we need to attribute each to whichever month its rows
// landed in).
export function filesSeenByMonth(
  perFile: Array<{ filename: string; portal: string; transactions: Transaction[] }>,
): Map<string, Array<{ file: string; portal: string }>> {
  const out = new Map<string, Array<{ file: string; portal: string }>>();
  for (const r of perFile) {
    if (!r.transactions || r.transactions.length === 0) continue;
    const months = new Set(r.transactions.map((t) => monthKey(t.date)));
    for (const m of months) {
      const arr = out.get(m) ?? [];
      arr.push({ file: r.filename, portal: r.portal });
      out.set(m, arr);
    }
  }
  return out;
}
